import { useContext } from 'react'
import { AuthContext } from '../../modules/auth/authContext'
import { Icon } from '../Icon'
import { navigationConfig } from './config'
import { DEFAULT_LANDING_PATH } from './paths'
import { NavMain } from '../navbar/NavMain'
import { NavUser } from '../navbar/NavUser'
import { NavSlack } from '../navbar/NavSlack'
import { NavReportIssue } from '../navbar/NavReportIssue'
import { canAccessSettingsRoute } from '@/lib/settings/settingsAccess'
import { isInternalUser } from '@storefronts/common-types'

import {
    Sidebar,
    SidebarProvider,
    SidebarHeader,
    SidebarContent,
    SidebarFooter,
    SidebarMenu,
    SidebarPinButton,
    SidebarTrigger,
    SidebarGroup,
    SidebarGroupContent,
    SidebarMenuItem,
    useSidebar,
} from '../ui/sidebar'

import { TooltipProvider } from '../ui/tooltip'
import { LocalStorageKey } from '@/lib/storageConstants'
import { cn } from '@/lib/utils'
import { getClientSwitchRedirectPath } from './clientScopedRoutes'
import {
    SIDEBAR_ICON_SLOT_CLASS,
    SIDEBAR_RAIL_HIDE_CLASS,
    SIDEBAR_WORDMARK_CLASS,
    SIDEBAR_WORDMARK_NUDGE_CLASS,
} from '../ui/sidebarExpandState'

function SideNavigationWordmark() {
    return (
        <span className={SIDEBAR_WORDMARK_NUDGE_CLASS}>
            <span className={SIDEBAR_ICON_SLOT_CLASS}>
                <span className="h-5 w-[13px] overflow-hidden">
                    <span className={SIDEBAR_WORDMARK_CLASS}>
                        <Icon type="square-logo" color="currentColor" />
                    </span>
                </span>
            </span>
            <span
                className={cn(
                    '-ml-2.5 h-5 overflow-hidden',
                    SIDEBAR_RAIL_HIDE_CLASS,
                )}
            >
                <span className={cn('-ml-[18.5px]', SIDEBAR_WORDMARK_CLASS)}>
                    <Icon type="square-logo" color="currentColor" />
                </span>
            </span>
        </span>
    )
}

function SideNavigationHeader() {
    const { reserveExpandedLayout } = useSidebar()

    return (
        <SidebarHeader className="relative h-10 shrink-0 overflow-hidden p-0">
            <div
                className={cn(
                    'absolute right-1 top-1 z-10',
                    SIDEBAR_RAIL_HIDE_CLASS,
                )}
            >
                <SidebarPinButton />
            </div>
            <a
                href={DEFAULT_LANDING_PATH}
                onClick={reserveExpandedLayout}
                className="absolute inset-0 flex items-center text-sidebar-foreground hover:text-sidebar-accent-foreground"
            >
                <SideNavigationWordmark />
            </a>
        </SidebarHeader>
    )
}

export const SideNavigation = ({
    syncBannerVisible = false,
    collapseEnabled = true,
    provideDesktopSidebar = true,
}: {
    syncBannerVisible?: boolean
    collapseEnabled?: boolean
    provideDesktopSidebar?: boolean
}) => {
    const { client, user, isSuperUser } = useContext(AuthContext)
    const isOrganicClient = client?.leadSource === 'ORGANIC'
    const settingsAccess = { isSuperUser, isOrganicClient }
    const internalUser = isInternalUser(user?.username)

    const handleClientChange = (clientHash: string) => {
        window.localStorage.setItem(LocalStorageKey.CLIENT_HASH, clientHash)
        const redirectPath = getClientSwitchRedirectPath(
            window.location.pathname,
        )
        if (redirectPath) {
            window.location.href = redirectPath
        } else {
            window.location.reload()
        }
    }

    // Prepare user data for NavUser component
    const userData = {
        name: user?.name || 'User',
        email: user?.username || 'user@example.com',
        avatar: client?.imageUrl || '',
    }

    // Filter enabled items
    const mainItems = navigationConfig.mainItems.filter((item) => item.enabled)
    const userMenuItems = navigationConfig.userMenu.filter(
        (item) =>
            item.enabled &&
            (!item.internalOnly || internalUser) &&
            (!item.href.startsWith('/settings') ||
                canAccessSettingsRoute(item.href, settingsAccess)),
    )
    const onboardNewClientLink = navigationConfig.onboardNewClientLink?.enabled
        ? navigationConfig.onboardNewClientLink
        : null

    const desktopSidebar = (
        <Sidebar
            variant="inset"
            collapsible={collapseEnabled ? 'icon' : 'none'}
            className={cn('h-full', syncBannerVisible && 'top-12 h-auto')}
            side="left"
        >
            <SideNavigationHeader />

            <SidebarContent className="gap-1">
                <NavMain items={mainItems} />
            </SidebarContent>

            <SidebarFooter className="gap-2 p-0 py-2">
                <SidebarGroup className="px-0 py-1.5">
                    <SidebarGroupContent>
                        <SidebarMenu className="gap-0">
                            <SidebarMenuItem>
                                <NavSlack />
                            </SidebarMenuItem>
                            <SidebarMenuItem>
                                <NavReportIssue />
                            </SidebarMenuItem>
                        </SidebarMenu>
                    </SidebarGroupContent>
                </SidebarGroup>
                <SidebarGroup className="px-0 py-1.5">
                    <NavUser
                        user={userData}
                        client={client}
                        menuItems={userMenuItems}
                        onClientChange={handleClientChange}
                        onboardNewClientLink={onboardNewClientLink}
                    />
                </SidebarGroup>
            </SidebarFooter>
        </Sidebar>
    )

    return (
        <>
            <div className="hidden h-full md:block">
                <TooltipProvider delayDuration={200}>
                    {provideDesktopSidebar ? (
                        <SidebarProvider
                            hoverExpand={collapseEnabled}
                            defaultOpen={!collapseEnabled}
                            className="h-full min-h-0 w-auto"
                        >
                            {desktopSidebar}
                        </SidebarProvider>
                    ) : (
                        desktopSidebar
                    )}
                </TooltipProvider>
            </div>

            {/* Mobile Navigation - From Right */}
            <div className="block size-full md:hidden">
                <TooltipProvider delayDuration={200}>
                    <SidebarProvider>
                        <div
                            className={cn(
                                'fixed left-4 z-[60]',
                                syncBannerVisible ? 'top-[4.5rem]' : 'top-6',
                            )}
                        >
                            <SidebarTrigger
                                className="rounded-md border p-2 shadow-md bg-card border-border"
                                size="lg"
                            />
                        </div>
                        <Sidebar variant="inset" className="h-full bg-muted">
                            <SideNavigationHeader />

                            <SidebarContent className="gap-1">
                                <NavMain items={mainItems} />
                            </SidebarContent>

                            <SidebarFooter className="gap-2 p-0 pt-2">
                                <SidebarGroup className="p-1.5">
                                    <SidebarGroupContent>
                                        <SidebarMenu className="gap-0">
                                            <SidebarMenuItem>
                                                <NavSlack />
                                            </SidebarMenuItem>
                                            <SidebarMenuItem>
                                                <NavReportIssue />
                                            </SidebarMenuItem>
                                        </SidebarMenu>
                                    </SidebarGroupContent>
                                </SidebarGroup>
                                <SidebarGroup className="p-1.5">
                                    <NavUser
                                        user={userData}
                                        client={client}
                                        menuItems={userMenuItems}
                                        onClientChange={handleClientChange}
                                        onboardNewClientLink={
                                            onboardNewClientLink
                                        }
                                    />
                                </SidebarGroup>
                            </SidebarFooter>
                        </Sidebar>
                    </SidebarProvider>
                </TooltipProvider>
            </div>
        </>
    )
}
